import { useState } from "react";
import type { ConversionConfig } from "../../types/sigwaz";

interface Props {
  config: ConversionConfig;
  onChange: (cfg: ConversionConfig) => void;
  onClose: () => void;
}

const SIGMA_STATUSES = ["experimental", "test", "stable", "deprecated", "unsupported"] as const;
const LEVEL_OPTIONS  = ["", "low", "medium", "high", "critical"] as const;
const LEVEL_LABELS: Record<string, string> = {
  "":         "All levels",
  "low":      "Low and above",
  "medium":   "Medium and above",
  "high":     "High and above",
  "critical": "Critical only",
};

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="cfg-chk" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => onChange(!checked)}>
      <div className={`chkbox${checked ? " on" : ""}`}>
        {checked && (
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path d="M1 4l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <span style={{ fontSize: 12, color: "var(--t2)" }}>{label}</span>
    </label>
  );
}

function NumberField({ label, value, onChange, min = 1, max = 999999999 }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number;
}) {
  return (
    <div className="cfg-item" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
      <span style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500 }}>{label}</span>
      <input
        className="cfg-inp"
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Math.max(min, parseInt(e.target.value) || min))}
        style={{ width: 90 }}
      />
    </div>
  );
}

function Section({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
      color: "var(--accent)", borderBottom: "1px solid var(--bd)", paddingBottom: 6,
      marginTop: 20, marginBottom: hint ? 6 : 10,
    }}>
      {title}
      {hint && (
        <div style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0, textTransform: "none", color: "var(--t3)", marginTop: 3 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function KeyValueEditor({ label, value, onChange }: {
  label: string;
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}) {
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");
  const entries = Object.entries(value);

  const add = () => {
    if (!newKey.trim()) return;
    onChange({ ...value, [newKey.trim()]: newVal.trim() });
    setNewKey(""); setNewVal("");
  };
  const remove = (k: string) => {
    const next = { ...value };
    delete next[k];
    onChange(next);
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500, marginBottom: 6 }}>{label}</div>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}>
          <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, color: "var(--accent)", flex: 1 }}>{k}</span>
          <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, color: "var(--t2)", flex: 2 }}>{v}</span>
          <button onClick={() => remove(k)} className="btn-ghost" style={{ padding: "2px 6px", fontSize: 10 }}>✕</button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input className="cfg-inp" placeholder="product" value={newKey}
          onChange={(e) => setNewKey(e.target.value)} style={{ flex: 1, fontSize: 11 }} />
        <input className="cfg-inp" placeholder="SID list" value={newVal}
          onChange={(e) => setNewVal(e.target.value)} style={{ flex: 2, fontSize: 11 }} />
        <button onClick={add} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}>+</button>
      </div>
    </div>
  );
}

export function SettingsModal({ config, onChange, onClose }: Props) {
  const set = <K extends keyof ConversionConfig>(key: K, val: ConversionConfig[K]) =>
    onChange({ ...config, [key]: val });
  const setLevel = (key: keyof typeof config.levels, val: number) =>
    onChange({ ...config, levels: { ...config.levels, [key]: val } });

  const toggleStatus = (status: string, exclude: boolean) => {
    const next = exclude
      ? [...config.excludedStatuses, status]
      : config.excludedStatuses.filter(s => s !== status);
    set("excludedStatuses", next);
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.55)", backdropFilter: "blur(4px)" }} onClick={onClose} />

      <div style={{
        position: "relative", zIndex: 1,
        width: 460, height: "100%", overflowY: "auto",
        background: "var(--s0)", borderLeft: "1px solid var(--bd)",
        padding: "24px 20px",
        display: "flex", flexDirection: "column", gap: 0,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>⚙ Settings</span>
          <button className="btn-ghost" onClick={onClose} style={{ padding: "4px 10px" }}>✕ Close</button>
        </div>
        <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 12 }}>
          All options apply to every conversion in this session.
        </div>

        {/* 1 ── Rule ID ──────────────────────────────────────────────────── */}
        <Section title="Rule ID" />
        <NumberField label="Rule ID Start" value={config.ruleIdStart} onChange={(v) => set("ruleIdStart", v)} />
        <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 4 }}>
          ID range preview: {config.ruleIdStart + 1} → {config.ruleIdStart + 9999}
        </div>

        {/* 2 ── Severity Levels ──────────────────────────────────────────── */}
        <Section title="Severity Levels" hint="Map Sigma severity to Wazuh rule level (0–15)." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {(["informational", "low", "medium", "high", "critical"] as const).map((lvl) => (
            <NumberField
              key={lvl}
              label={lvl.charAt(0).toUpperCase() + lvl.slice(1)}
              value={config.levels[lvl]}
              onChange={(v) => setLevel(lvl, v)}
              min={0} max={15}
            />
          ))}
        </div>

        {/* 3 ── Status Filter ────────────────────────────────────────────── */}
        <Section title="Status Filter" hint="Exclude rules whose Sigma status matches." />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 4 }}>
          {SIGMA_STATUSES.map((status) => (
            <Toggle
              key={status}
              label={`Exclude ${status}`}
              checked={config.excludedStatuses.includes(status)}
              onChange={(v) => toggleStatus(status, v)}
            />
          ))}
        </div>

        {/* 4 ── Rule Filters ─────────────────────────────────────────────── */}
        <Section title="Rule Filters" hint="Skip rules that don't meet these criteria." />

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500, marginBottom: 6 }}>
            Minimum severity level
          </div>
          <select
            className="cfg-inp"
            value={config.minLevel}
            onChange={(e) => set("minLevel", e.target.value)}
            style={{ width: "100%" }}
          >
            {LEVEL_OPTIONS.map(lvl => (
              <option key={lvl} value={lvl} style={{ background: "var(--s1, #1e2433)", color: "var(--t1)" }}>
                {LEVEL_LABELS[lvl]}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 4 }}>
            Rules below this level are skipped (counted as skipped, not errors).
          </div>
        </div>

        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500, marginBottom: 6 }}>
            Allowed products <span style={{ fontWeight: 400, opacity: .7 }}>(empty = all)</span>
          </div>
          <input
            className="cfg-inp"
            value={config.allowedProducts.join(", ")}
            onChange={(e) => set("allowedProducts", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
            placeholder="windows, linux, aws, azure…"
            style={{ width: "100%", fontSize: 11 }}
          />
          <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 4 }}>
            Comma-separated logsource product names. Rules for unlisted products are skipped.
          </div>
        </div>

        {/* 5 ── Output Options ───────────────────────────────────────────── */}
        <Section title="Output Options" />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Toggle label="no_full_log" checked={config.noFullLog} onChange={(v) => set("noFullLog", v)} />
          <Toggle label="email_alert" checked={config.emailAlert} onChange={(v) => set("emailAlert", v)} />
        </div>

        {config.emailAlert && (
          <>
            <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 10, marginBottom: 4 }}>
              Email alert levels (comma-separated):
            </div>
            <input
              className="cfg-inp"
              value={config.emailLevels.join(", ")}
              onChange={(e) => set("emailLevels", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
              style={{ width: "100%", fontSize: 11 }}
            />
          </>
        )}

        {/* 6 ── File Splitting ───────────────────────────────────────────── */}
        <Section title="File Splitting" hint="Split large exports to avoid Wazuh OOM on import." />
        <NumberField
          label="Max rules per XML file (0 = no split)"
          value={config.splitSize}
          onChange={(v) => set("splitSize", v)}
          min={0}
        />

        {/* 7 ── SID / Group Overrides ────────────────────────────────────── */}
        <Section title="Parent Rule Overrides (if_sid)" />
        <KeyValueEditor
          label="product → SID list"
          value={config.ifSidOverrides}
          onChange={(v) => set("ifSidOverrides", v)}
        />

        <Section title="Group Overrides (if_group)" />
        <KeyValueEditor
          label="product → group name"
          value={config.ifGroupOverrides}
          onChange={(v) => set("ifGroupOverrides", v)}
        />

        <div style={{ flex: 1 }} />
        <div style={{ borderTop: "1px solid var(--bd)", paddingTop: 16, marginTop: 20 }}>
          <button
            className="btn-convert"
            style={{ width: "100%", justifyContent: "center" }}
            onClick={onClose}
          >
            ✓ Apply & Close
          </button>
        </div>
      </div>
    </div>
  );
}
